import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosConfig";
import { dateToYmd } from "../../utils/dateInput";
import FormSelect from "../components/FormSelect";
import { months } from "../questionnaire_constants";
import { CHECKIN_INACTIVE, setCheckinToken } from "../utils/checkinToken";

// STI-3024: a check-in link without a valid token (sent before tokens, or expired) lands here. The
// patient confirms their date of birth and gets a fresh token for this appointment. The backend gives
// a wrong date and an unknown appointment the same answer and limits attempts per appointment.
// A cancelled or rescheduled appointment (`?reason=appointment_inactive`, or a 410 from /session) gets
// a message instead: a new token would not open it.

const SESSION_URL = "/api/athena/digital-checkin/session";

export const VERIFY_MESSAGES = {
  mismatch: "Those details don't match our records. Please check the date of birth and try again.",
  tooMany: "Too many attempts. Please try again in an hour, or call the clinic.",
  unavailable: "Check-in verification isn't available right now. Please try again later, or call the clinic.",
  generic: "Something went wrong. Please try again, or call the clinic.",
  inactive:
    "This appointment has been cancelled or rescheduled. Please use the link from your most recent appointment message, or call the clinic.",
  missingDate: "Please enter your date of birth.",
  invalidDate: "Please enter a valid date of birth.",
  futureDate: "Date of birth can't be in the future.",
};

/** The entered day, month and year as YYYY-MM-DD (what /session parses), or null if not a real date. */
function isoDate(day: string, month: string, year: string): string | null {
  if (!/^\d{1,2}$/.test(day) || !/^\d{1,2}$/.test(month) || !/^\d{4}$/.test(year)) {
    return null;
  }
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (date.getFullYear() !== Number(year) || date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) {
    return null;
  }
  return dateToYmd(date);
}

const fieldClassName =
  "w-full rounded-md border border-[#DCE1E8] py-3 px-3 text-base text-[#1C1C1C] placeholder-[#818BA0] focus:border-[#01545D]";

const CheckinVerify: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [inactive, setInactive] = useState(
    () => new URLSearchParams(location.search).get("reason") === CHECKIN_INACTIVE
  );
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!appointmentId) {
      setError(VERIFY_MESSAGES.generic);
      return;
    }
    if (!dobDay && !dobMonth && !dobYear) {
      setError(VERIFY_MESSAGES.missingDate);
      return;
    }
    const dateOfBirth = isoDate(dobDay.trim(), dobMonth, dobYear.trim());
    if (!dateOfBirth) {
      setError(VERIFY_MESSAGES.invalidDate);
      return;
    }
    if (dateOfBirth > dateToYmd(new Date())) {
      setError(VERIFY_MESSAGES.futureDate);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        SESSION_URL,
        { appointmentId, dateOfBirth },
        { headers: { client: "axis", domain: "axis" }, skipGlobalError: true } as any
      );
      const token = response.data?.token;
      if (typeof token !== "string" || !token) {
        setError(VERIFY_MESSAGES.generic);
        return;
      }
      // The start page reads the kept token, so it never needs to go back into the address bar.
      setCheckinToken(token);
      navigate(`/start-checkin/${encodeURIComponent(appointmentId)}`, { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) {
        setError(VERIFY_MESSAGES.mismatch);
      } else if (status === 410) {
        setInactive(true);
      } else if (status === 429) {
        setError(VERIFY_MESSAGES.tooMany);
      } else if (status === 503) {
        setError(VERIFY_MESSAGES.unavailable);
      } else {
        setError(VERIFY_MESSAGES.generic);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center justify-center px-4 py-10">
        {inactive ? (
          <div className="flex flex-col items-center max-w-md w-full text-center">
            <div className="text-3xl font-semibold text-[#01545D] mb-3">This link is no longer active</div>
            <div role="alert" className="text-base text-[#1C1C1C]">
              {VERIFY_MESSAGES.inactive}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col items-center max-w-md w-full text-center" noValidate>
            <div className="text-3xl font-semibold text-[#01545D] mb-3">Confirm it's you</div>
            <div className="text-base text-[#1C1C1C] mb-6">
              To keep your information private, please enter your date of birth to continue your check-in.
            </div>
            {/* Day / month / year, the same fields the check-in's own date of birth uses (PersonalInformation). */}
            <fieldset className="w-full mb-4" disabled={submitting}>
              <legend className="text-left text-sm font-medium text-[#1C1C1C] mb-2">Date of birth</legend>
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    id="checkin-dob-day"
                    type="number"
                    inputMode="numeric"
                    aria-label="Day"
                    placeholder="DD"
                    min={1}
                    max={31}
                    value={dobDay}
                    onChange={(e) => setDobDay(e.target.value)}
                    className={fieldClassName}
                  />
                </div>
                <div className="flex-1">
                  <FormSelect
                    label=""
                    id="checkin-dob-month"
                    aria-label="Month"
                    options={months}
                    value={dobMonth}
                    onChange={(e) => setDobMonth(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <input
                    id="checkin-dob-year"
                    type="number"
                    inputMode="numeric"
                    aria-label="Year"
                    placeholder="YYYY"
                    min={1900}
                    max={new Date().getFullYear()}
                    value={dobYear}
                    onChange={(e) => setDobYear(e.target.value)}
                    className={fieldClassName}
                  />
                </div>
              </div>
            </fieldset>
            {error && (
              <div role="alert" className="text-sm text-[#B42318] mb-4">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 bg-[#01545D] text-white rounded-full font-semibold disabled:opacity-60"
            >
              {submitting ? "Checking..." : "Continue"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default CheckinVerify;
